import { useState } from 'react';

import type { ApiEndpoint, HealthSummary } from '../../api/monitor';
import { HEALTH_TONE, OVERALL_TONE, formatCheckedAt, overallLabel } from '../../lib/health';
import { NavIcon } from './NavIcon';

interface HealthIndicatorProps {
  summary: HealthSummary | null;
  apis: ApiEndpoint[];
  /** Leva para a tela de Status a partir do rodapé do balão. */
  onOpenStatus: () => void;
}

const PULSE_ICON = 'M3 12h4l2-5 4 10 2-5h6';

/**
 * Indicador de saúde geral no canto do cabeçalho: verde com tudo no ar,
 * amarelo quando parte das APIs está fora, vermelho quando nenhuma responde.
 * O balão detalha quem está com problema.
 */
export function HealthIndicator({ summary, apis, onOpenStatus }: HealthIndicatorProps) {
  const [open, setOpen] = useState(false);

  const status = summary?.status ?? 'unknown';
  const tone = OVERALL_TONE[status];
  const label = summary ? overallLabel(summary) : 'Carregando o status das APIs…';

  // Com algo fora do ar o balão mostra só os problemas; senão, a lista inteira.
  const problems = apis.filter((api) => api.active && api.health.status !== 'up');
  const rows = problems.length > 0 ? problems : apis.filter((api) => api.active);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        title="Status das APIs"
        aria-expanded={open}
        className={`relative block p-1.5 hover:opacity-75 ${tone.icon}`}
      >
        <NavIcon path={PULSE_ICON} size={21} />
        <span
          className={`absolute top-1 right-0.5 size-2 rounded-full border-2 border-white ${tone.dot}`}
        />
        <span className="sr-only">{label}</span>
      </button>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="animate-rise-fast border-line-200 shadow-popover absolute top-[calc(100%+10px)] -right-10 z-40 w-[min(340px,calc(100vw-32px))] overflow-hidden rounded-2xl border bg-white">
            <div className="border-surface-200 flex items-center gap-2 border-b px-3.5 py-3">
              <span className={`size-2.5 shrink-0 rounded-full ${tone.dot}`} />
              <div className="flex-1 text-[13px] font-extrabold">{label}</div>
            </div>

            <div className="max-h-[280px] overflow-auto">
              {rows.length === 0 ? (
                <div className="text-ink-600 px-3.5 py-4 text-[12.5px]">
                  Nenhuma API cadastrada ainda.
                </div>
              ) : (
                rows.map((api) => {
                  const health = HEALTH_TONE[api.health.status];

                  return (
                    <div key={api.id} className="border-surface-50 border-b px-3.5 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`size-[7px] shrink-0 rounded-full ${health.dot}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] font-extrabold">{api.name}</div>
                          <div className="text-ink-400 truncate font-mono text-[11px]">
                            {api.url}
                          </div>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${health.pill}`}
                        >
                          {health.label}
                        </span>
                      </div>

                      {api.health.status === 'down' && api.health.error ? (
                        <div className="text-danger-600 mt-1.5 ml-[15px] text-[11.5px] font-semibold">
                          {api.health.error}
                        </div>
                      ) : null}

                      <div className="text-ink-400 mt-1 ml-[15px] text-[11px]">
                        {formatCheckedAt(api.health.checkedAt)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenStatus();
              }}
              className="border-surface-200 bg-surface-100 text-navy-800 hover:bg-brand-100/30 w-full border-t p-3 text-[12.5px] font-extrabold"
            >
              Ver página de status
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
